import { produce } from 'immer';

import { NotificationChannel, NotificationSettings } from '../types';
import { NOTIFICATION_CONFIG } from './notification-settings-mapper';

/**
 * Toggle a specific topic + channel combination
 */
export function toggleSetting(settings: NotificationSettings, topicId: string, channel: NotificationChannel): NotificationSettings {
	return produce(settings, (draft) => {
		const setting = draft.find((s) => s.topicId === topicId && s.channel === channel);
		if (setting) {
			setting.enabled = !setting.enabled;
		}
	});
}

/**
 * Toggle all settings in a category for a specific channel
 */
export function toggleCategoryChannel(settings: NotificationSettings, categoryId: string, channel: NotificationChannel): NotificationSettings {
	// Find all topic IDs in this category
	const category = NOTIFICATION_CONFIG.categories.find((c) => c.id === categoryId);
	if (!category) return settings;

	const topicIds = category.topics.map((t) => t.id);

	// Find settings for this category + channel combination
	const categoryChannelSettings = settings.filter((s) => topicIds.includes(s.topicId) && s.channel === channel);

	// Check if all are enabled
	const allEnabled = categoryChannelSettings.length > 0 && categoryChannelSettings.every((s) => s.enabled);

	// Toggle all to opposite state
	return produce(settings, (draft) => {
		draft.forEach((setting) => {
			if (topicIds.includes(setting.topicId) && setting.channel === channel) {
				setting.enabled = !allEnabled;
			}
		});
	});
}

/**
 * Check if all settings in a category for a channel are enabled
 */
export function isCategoryChannelChecked(settings: NotificationSettings, categoryId: string, channel: NotificationChannel): boolean {
	const category = NOTIFICATION_CONFIG.categories.find((c) => c.id === categoryId);
	if (!category) return false;

	const topicIds = category.topics.map((t) => t.id);
	const categoryChannelSettings = settings.filter((s) => topicIds.includes(s.topicId) && s.channel === channel);

	return categoryChannelSettings.length > 0 && categoryChannelSettings.every((s) => s.enabled);
}

/**
 * Check if category + channel checkbox should be indeterminate (some but not all enabled)
 */
export function isCategoryChannelIndeterminate(settings: NotificationSettings, categoryId: string, channel: NotificationChannel): boolean {
	const category = NOTIFICATION_CONFIG.categories.find((c) => c.id === categoryId);
	if (!category) return false;

	const topicIds = category.topics.map((t) => t.id);
	const categoryChannelSettings = settings.filter((s) => topicIds.includes(s.topicId) && s.channel === channel);

	if (categoryChannelSettings.length === 0) return false;

	const enabledCount = categoryChannelSettings.filter((s) => s.enabled).length;
	return enabledCount > 0 && enabledCount < categoryChannelSettings.length;
}

/**
 * Get the setting value for a specific topic + channel
 */
export function getSetting(settings: NotificationSettings, topicId: string, channel: NotificationChannel): boolean {
	const setting = settings.find((s) => s.topicId === topicId && s.channel === channel);
	return setting?.enabled ?? false;
}
